import { requireService } from '@profgeosoft/di-ez';
import { Drawer, Table, useGridApiRef } from '@profgeosoft-ui/react';
import clsx from 'clsx';
import { useCallback, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { wrap } from 'src/packages/mobx-di/wrap';
import { hasValue } from 'src/packages/utils/has-value';
import { MultiComboboxField } from 'src/services/directory-service/entities/controls/multicombobox-field';

import type {
  GridCellEditStartParams,
  GridCellEditStopParams,
  GridCellParams,
  GridColumnResizeParams,
  GridSortItem,
  MuiBaseEvent,
  MuiEvent,
} from '@profgeosoft-ui/react';
import type { MainDirectoryService } from 'src/services/directory-service';
import type { DirectoryRecord } from 'src/services/directory-service/entities/directory-record.entity';
import type { TTableSortItem } from 'src/services/table-settings-service/table-settings-service';

import { DisplaySettings } from '../display-settings/display-settings';

import { SERVICE_COLUMNS } from './consts';
import { DirectoryTableMediator } from './directory-table-mediator';
import { tableSx } from './table-sx';
import { useGetColumnsGrouping } from './use-get-columns-grouping';
import { useTableColumns } from './use-table-columns';

import styles from './directory-table.module.scss';

type Props = {
  directory: MainDirectoryService;
  isRowSelectionModeOn: boolean;
};

const muiLanguageKeys: Record<string, 'ruRU' | 'enEN' | 'esES'> = {
  ru: 'ruRU',
  en: 'enEN',
  es: 'esES',
};

const MIN_VISIBLE_COLUMNS = 3;

export const DirectoryTable = wrap<Props>(function DirectoryTable({ directory, isRowSelectionModeOn }) {
  const displayManager = directory.tableSettingsManager;
  const notifications = requireService('notificationsService');
  const localization = requireService('localizationService');
  const directoryPage = requireService('directoryPage');

  const tableApi = useGridApiRef();
  const columns = useTableColumns(directory);
  const groupingModel = useGetColumnsGrouping(directory.view);

  const { t } = useTranslation('directory');

  const handleWidthChange = ({ colDef: { field }, width }: GridColumnResizeParams) => {
    if (!displayManager) {
      return;
    }

    const columns = tableApi.current.getAllColumns();
    const updatedColumnsWidths: Record<string, number> = {};

    columns.forEach((column) => {
      if (column.field === field) {
        updatedColumnsWidths[field] = width;
      } else if (displayManager.settings.columnsWidth && !displayManager.settings.columnsWidth[column.field]) {
        updatedColumnsWidths[column.field] = column.computedWidth;
      }
    });

    displayManager.onWidthChange(updatedColumnsWidths);
  };

  const handleTableSettingsClick = useCallback(() => {
    displayManager?.setSettingsOpen(true);
    tableApi.current.hideColumnMenu();
  }, [displayManager, tableApi]);

  const slotProps = useMemo(
    () => ({
      columnMenu: {
        onTableSettingsClick: handleTableSettingsClick,
        minVisibleColumns: MIN_VISIBLE_COLUMNS,
      },
    }),
    [handleTableSettingsClick],
  );

  const convertToTableSortItems = (model: GridSortItem[]): TTableSortItem[] => {
    const convertedSortItems: TTableSortItem[] = [];

    model.forEach((item) => {
      if (item.sort === 'asc' || item.sort === 'desc') {
        convertedSortItems.push({
          field: item.field,
          sort: item.sort,
        });
      }
    });

    return convertedSortItems;
  };

  const getCellClassName = useCallback(
    (params: GridCellParams<DirectoryRecord>): string => {
      const control = params.row.controlsMap.get(params.field);

      if (!control) {
        return '';
      }

      const isSelected =
        displayManager?.selectedCellData &&
        displayManager?.selectedCellData.cellField === params.field &&
        displayManager?.selectedCellData.rowId === params.row.recordId;

      return clsx(
        styles.cell,
        control.error && 'go-table__error-cell',
        isSelected && `${styles.selectedCell} Mui-selected`,
        directoryPage.isEditing && (control.isDisabled || control.isEnteringBlocked) && styles.blockedCell,
      );
    },
    [directoryPage.isEditing, displayManager?.selectedCellData],
  );

  const handleCellDeletePress = (params: GridCellEditStartParams, event: MuiEvent<MuiBaseEvent>) => {
    if (params.reason === 'deleteKeyDown') {
      const cellSelectionModel = tableApi.current.unstable_getCellSelectionModel();

      const removeValue = (fieldId: string, recordId: number) => {
        directory.directoryViewMediator?.onControlValueChange(recordId, fieldId, null);
      };

      const rowIds = Object.keys(cellSelectionModel);

      if (!rowIds.length) {
        return;
      }

      if (rowIds.length > 1 || Object.keys(cellSelectionModel[rowIds[0]]).length) {
        event.defaultMuiPrevented = true;
        const rows: DirectoryRecord[] = [];

        for (const recordId in cellSelectionModel) {
          if (Number.isNaN(Number(recordId))) {
            console.error('record id is not valid');
            continue;
          }

          const row = tableApi.current.getRow(Number(recordId));

          if (!row) {
            continue;
          }

          rows.push(row);

          for (const fieldId in cellSelectionModel[recordId]) {
            removeValue(fieldId, Number(recordId));
          }
        }

        tableApi.current.updateRows(rows);
      } else {
        removeValue(params.colDef.field, params.row.recordId);
      }
    }
  };

  const handleComboboxEnterPress = (params: GridCellEditStopParams, event: MuiEvent<MuiBaseEvent>) => {
    if (params.reason === 'enterKeyDown') {
      const control = params?.row?.controlsMap.get(params.colDef.field);

      if (control instanceof MultiComboboxField) {
        event.defaultMuiPrevented = true;
      }
    }
  };

  useEffect(() => {
    if (!displayManager || !displayManager.settings?.columnsWidth) {
      return;
    }

    for (const column of tableApi.current.getAllColumns()) {
      if (
        displayManager.settings.columnsWidth[column.field] === column.width ||
        !hasValue(displayManager.settings.columnsWidth[column.field])
      ) {
        continue;
      }

      tableApi.current.setColumnWidth(column.field, displayManager.settings.columnsWidth[column.field]);
    }
  }, [tableApi, columns, displayManager, displayManager?.settings]);

  useEffect(() => {
    if (!displayManager) {
      return;
    }

    if (!displayManager.settings.columnsOrderModel) {
      displayManager.onColumnsOrderModelChange(columns.map((columns) => columns.field));
    }
  }, [columns, displayManager, tableApi]);

  useEffect(() => {
    directory.setDirectoryViewMediator(new DirectoryTableMediator(directory, tableApi, notifications, localization));
  }, [directory, localization, notifications, tableApi]);

  useEffect(() => {
    let timer: number | undefined;

    const handlePaste = (event: KeyboardEvent) => {
      if (event.code !== 'KeyV' || (!event.ctrlKey && !event.metaKey)) {
        return;
      }

      // игнорируем, если событие реагирует на вставку текста в input поле т.к. в таком случае должна отработать дефолтная логика вставки браузера
      if (event.target instanceof HTMLInputElement) {
        return;
      }

      if (hasValue(timer)) {
        return;
      }

      event.preventDefault();

      directory.directoryViewMediator?.pasteData(displayManager?.selectedCellData || undefined);
      timer = window.setTimeout(() => {
        timer = undefined;
      }, 300);
    };

    document.addEventListener('keydown', handlePaste);

    return () => {
      document.removeEventListener('keydown', handlePaste);
      window.clearTimeout(timer);
    };
  }, [directory.directoryViewMediator, displayManager]);

  useEffect(() => {
    let timer: number | undefined;

    const handleCopy = (event: KeyboardEvent) => {
      if (event.code !== 'KeyC' || (!event.ctrlKey && !event.metaKey)) {
        return;
      }

      if (hasValue(timer)) {
        return;
      }

      const selectedRows = tableApi.current.getSelectedRows();

      if (selectedRows.size < 1) {
        const cellSelection = tableApi.current.unstable_getCellSelectionModel();

        const tableCopyTargetData = (() => {
          if (cellSelection && !!Object.keys(cellSelection).length) {
            return cellSelection;
          }

          if (displayManager?.selectedCellData) {
            return {
              [displayManager.selectedCellData.rowId]: {
                [displayManager.selectedCellData.cellField]: true,
              },
            };
          }

          return null;
        })();

        if (!tableCopyTargetData) {
          return;
        }

        directory.directoryViewMediator?.copySelectedCellData(tableCopyTargetData);

        timer = window.setTimeout(() => {
          timer = undefined;
        }, 500);

        return;
      }

      directory.directoryViewMediator?.copySelectedRowsToClipboard();

      timer = window.setTimeout(() => {
        timer = undefined;
      }, 300);
    };

    document.addEventListener('keydown', handleCopy);

    return () => {
      document.removeEventListener('keydown', handleCopy);
      window.clearTimeout(timer);
    };
  }, [directory.directoryViewMediator, displayManager, tableApi]);

  useEffect(() => {
    const handleFocusIn = ({ field, row }: GridCellParams) => {
      const focusedCellData = { cellField: field, rowId: row.recordId };
      displayManager?.setSelectedCellData(focusedCellData);
    };

    return tableApi.current.subscribeEvent('cellFocusIn', handleFocusIn);
  }, [displayManager, tableApi]);

  if (!displayManager) {
    return null;
  }

  return (
    <>
      <div className={styles.tableWrapper}>
        <Table
          initialState={{
            filter: {
              filterModel: displayManager.settings.filterModel,
            },
            sorting: {
              sortModel: displayManager.settings.sortingModel,
            },
          }}
          apiRef={tableApi}
          columns={columns}
          rows={directory.records}
          sx={tableSx}
          language={muiLanguageKeys[localization.language]}
          valueDragging
          onValueDraggingEnd={directory.directoryViewMediator?.handleValueDraggingEnd}
          serviceColumns={SERVICE_COLUMNS}
          columnsOrderModel={displayManager.settings.columnsOrderModel}
          columnVisibilityModel={displayManager.settings.columnsVisibility}
          pinnedColumns={displayManager.settings.pinnedColumns}
          onCellEditStop={handleComboboxEnterPress}
          onCellEditStart={handleCellDeletePress}
          onFilterModelChange={(model) => displayManager.onFilterChange(model)}
          onColumnVisibilityModelChange={displayManager.onColumnVisibilityChange}
          onPinnedColumnsChange={displayManager.onPinChange}
          onSortModelChange={(model) => displayManager.onSortChange(convertToTableSortItems(model))}
          onColumnResize={handleWidthChange}
          isCellEditable={(params) => {
            const row = directory.recordsMap.get(params.id as number);
            const control = row?.controlsMap.get(params.field);

            return !(control?.isDisabled || control?.isEnteringBlocked);
          }}
          onColumnsOrderModelChange={displayManager.onColumnsOrderModelChange}
          slotProps={slotProps}
          className={styles.table}
          checkboxSelection={isRowSelectionModeOn}
          disableAggregation
          disableRowGrouping
          unstable_cellSelection
          loading={directory.isDataFetching || directory.isAdditionalDirectoriesFetching}
          columnGroupingModel={groupingModel}
          experimentalFeatures={{ columnGrouping: true }}
          getRowId={(row) => row.recordId}
          onRowSelectionModelChange={(model) => directory.directoryViewMediator?.setIsCopyAvailable(!!model.length)}
          onClipboardCopy={(_, event) => {
            event.defaultMuiPrevented = true;
          }}
          onCellKeyDown={(_, event) => {
            if (event.code === 'KeyV' && (event.metaKey || event.ctrlKey)) {
              event.defaultMuiPrevented = true;
            }
          }}
          getCellClassName={getCellClassName}
          getRowClassName={({ id }) => {
            const classNames: string[] = [];

            if (typeof id === 'number') {
              const isActive = directory.recordsActivity[id];

              if (isActive) {
                classNames.push(clsx(styles.disactiveRow));
              }
            }

            return '';
          }}
        />
      </div>
      {tableApi.current && (
        <Drawer
          className={styles.displaySettingsWrapper}
          style={{ width: 320 }}
          open={displayManager.isSettingsOpen}
          onClose={() => displayManager.setSettingsOpen(false)}
          title={<p className={styles.title}>{t('displaySettings:title')}</p>}
        >
          <DisplaySettings
            fields={directory.controlsViewsMap}
            columns={columns}
            displayManager={displayManager}
            minVisibleColumns={MIN_VISIBLE_COLUMNS}
          />
        </Drawer>
      )}
    </>
  );
});
